import QRCode from 'qrcode';
import { m } from 'framer-motion';
import { useState, useEffect } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import MuiEmailIcon from '@mui/icons-material/Email';
import MuiPhoneIcon from '@mui/icons-material/Phone';
import MuiPublicIcon from '@mui/icons-material/Public';
import MuiSecurityIcon from '@mui/icons-material/Security';
import MuiCheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Card,
  Grid,
  Stack,
  Alert,
  Divider,
  Skeleton,
  Typography,
  AlertTitle,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useDevicePermissions } from 'src/hooks/use-device-permissions';

import croppedLogo from 'src/assets/logo/logo.png';
import { CONFIG } from 'src/environments/environment';

import MuiIcon from 'src/components/mui-icon';
import { varBounce, MotionContainer } from 'src/components/animate';

import PermissionItem from './permission-item';

// ----------------------------------------------------------------------

const SUPPORT_PHONE = '612-473-5556';

// ----------------------------------------------------------------------

export default function PermissionRequestView() {
  const router = useRouter();
  const {
    permissions,
    isChecking,
    allGranted,
    requestPermission,
    requestAllPermissions,
    openAppSettings,
    openLocationSettings,
  } = useDevicePermissions();

  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Generate QR code for support
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const supportUrl = 'https://www.point-craft.com/support';
        const qrDataUrl = await QRCode.toDataURL(supportUrl, {
          width: 120,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });
        setQrCodeUrl(qrDataUrl);
      } catch (error) {
        console.error('Failed to generate QR code:', error);
      }
    };

    generateQRCode();
  }, []);

  // Redirect to dashboard when all permissions are granted
  useEffect(() => {
    if (allGranted) {
      console.log('[Permissions] All permissions granted, redirecting to dashboard...');
      router.replace(paths.dashboard.root);
    }
  }, [allGranted, router]);

  const grantedCount = permissions.filter((p) => p.status === 'granted').length;
  const totalCount = permissions.length;
  const hasDenied = permissions.some((p) => p.status === 'denied');
  const hasLocationServicesDisabled = permissions.some(
    (p) => p.id === 'location' && p.status === 'settings-disabled'
  );

  let primaryAction = requestAllPermissions;
  let primaryActionLabel = 'Continue';

  if (hasLocationServicesDisabled) {
    primaryAction = openLocationSettings;
    primaryActionLabel = 'Turn On Location';
  } else if (hasDenied) {
    primaryAction = openAppSettings;
    primaryActionLabel = 'Open Settings';
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
      }}
    >
      {/* Header with Logo */}
      <Box
        sx={{
          pb: 2,
          pt: 1,
          px: 3,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box component="img" src={croppedLogo} alt="Point Craft" sx={{ height: 40 }} />
            <Divider orientation="vertical" flexItem />
            <Typography variant="h5" color="primary.main" fontWeight={600}>
              Permissions Required
            </Typography>
          </Stack>
          <MuiIcon icon={MuiSecurityIcon} sx={{ color: 'primary.main', width: 32, height: 32 }} />
        </Stack>
      </Box>

      <MotionContainer sx={{ flex: 1, display: 'flex', p: 3, overflow: 'auto' }}>
        <m.div variants={varBounce().in} style={{ width: '100%' }}>
          <Grid container spacing={3} sx={{ height: '100%' }}>
            {/* Left Section - Permission Cards */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 3,
                  overflow: 'auto',
                }}
              >
                {/* Progress Indicator */}
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Permission Progress
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                      {grantedCount} / {totalCount}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'grey.200',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${(grantedCount / totalCount) * 100}%`,
                        backgroundColor: allGranted ? 'success.main' : 'primary.main',
                        borderRadius: 4,
                        transition: 'width 0.3s ease-in-out',
                      }}
                    />
                  </Box>
                </Box>

                {/* Info Alert */}
                <Alert severity="info" sx={{ mb: 3 }}>
                  <AlertTitle>Why do we need these permissions?</AlertTitle>
                  <Typography variant="body2">
                    PointCraft requires these permissions to enable payment processing, receipt
                    printing, and barcode scanning. Your data is never shared or stored beyond what
                    is necessary for these features.
                  </Typography>
                </Alert>

                {/* Denied Warning */}
                {hasDenied && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>Some permissions are denied</AlertTitle>
                    <Typography variant="body2">
                      One or more permissions have been denied. You&apos;ll need to enable them in
                      your device settings to continue using the app.
                    </Typography>
                  </Alert>
                )}

                {hasLocationServicesDisabled && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>Location Services are turned off</AlertTitle>
                    <Typography variant="body2">
                      Turn on Location Services in Android settings, then return to PointCraft to
                      continue.
                    </Typography>
                  </Alert>
                )}

                {/* Permission Cards */}
                <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
                  {permissions.map((permission) => (
                    <PermissionItem
                      key={permission.id}
                      permission={permission}
                      onGrant={() => requestPermission(permission.id)}
                      onOpenSettings={openAppSettings}
                      onOpenLocationSettings={openLocationSettings}
                      isLoading={isChecking}
                    />
                  ))}
                </Stack>

                {/* Primary Action Button */}
                <Box
                  sx={{
                    position: 'relative',
                    p: 1,
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-50%',
                      left: '-50%',
                      width: '200%',
                      height: '200%',
                      background: `conic-gradient(
                        from 0deg,
                        transparent 0deg,
                        #667eea 20deg,
                        #764ba2 45deg,
                        #667eea 70deg,
                        transparent 90deg,
                        transparent 360deg
                      )`,
                      animation: 'rotate 2s linear infinite',
                      zIndex: -1,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 3,
                      left: 3,
                      right: 3,
                      bottom: 3,
                      background: 'white',
                      borderRadius: 2,
                      zIndex: -1,
                    },
                    '@keyframes rotate': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(360deg)',
                      },
                    },
                  }}
                >
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    size="large"
                    color="primary"
                    onClick={primaryAction}
                    loading={isChecking}
                    disabled={allGranted}
                    startIcon={
                      <MuiIcon
                        icon={allGranted ? MuiCheckCircleIcon : MuiSecurityIcon}
                        sx={{ width: 28, height: 28 }}
                      />
                    }
                    sx={{
                      py: 2.5,
                      px: 4,
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      minHeight: 64,
                      background: allGranted
                        ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: allGranted
                          ? 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)'
                          : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                      },
                      '&.Mui-disabled': {
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        color: 'white',
                      },
                      '& .MuiLoadingButton-loadingIndicator': {
                        color: 'white',
                      },
                    }}
                  >
                    {(() => {
                      if (allGranted) return 'All Permissions Granted!';
                      return primaryActionLabel;
                    })()}
                  </LoadingButton>
                </Box>
              </Card>
            </Grid>

            {/* Right Section - Support Information */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  pt: 2,
                  backgroundColor: 'background.neutral',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h6" color="primary" sx={{ mb: 3, textAlign: 'center' }}>
                  Need Help?
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* QR Code */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Quick Support Access
                  </Typography>
                  {qrCodeUrl ? (
                    <Box
                      component="img"
                      src={qrCodeUrl}
                      alt="Support QR Code"
                      sx={{ width: 120, height: 120, mx: 'auto' }}
                    />
                  ) : (
                    <Skeleton variant="rectangular" width={120} height={120} sx={{ mx: 'auto' }} />
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Scan for Support
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Contact Info */}
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MuiIcon icon={MuiPhoneIcon} sx={{ color: 'primary.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Support Phone
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.8rem',
                            fontWeight: 'bold',
                            fontFamily: 'monospace',
                          }}
                        >
                          {SUPPORT_PHONE}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Press #2 for Technical Support
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MuiIcon icon={MuiEmailIcon} sx={{ color: 'primary.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Support Email
                        </Typography>
                        <Typography variant="body1">
                          {CONFIG.VITE_CUSTOMER_SERVICE_EMAIL || 'support@point-craft.com'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MuiIcon icon={MuiPublicIcon} sx={{ color: 'primary.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Online Support
                        </Typography>
                        <Typography
                          variant="body1"
                          color="primary"
                          sx={{
                            textDecoration: 'none',
                            fontWeight: 600,
                          }}
                        >
                          Point-Craft.com/Support
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>

                <Divider sx={{ my: 3 }} />

                {/* Permission Info */}
                <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ mb: 1, display: 'block' }}
                  >
                    HOW TO ENABLE PERMISSIONS
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      1. Tap &quot;Continue&quot; above
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      2. Tap &quot;Allow&quot; on each permission prompt
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      3. If denied, tap &quot;Open Settings&quot; and enable manually
                    </Typography>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </m.div>
      </MotionContainer>
    </Box>
  );
}
